<template>
  <div class="ip-connect">
    <div class="ip-connect__emblem">
      <i class="fas fa-project-diagram"></i>
    </div>

    <div class="ip-connect__body">
      <h6 class="ip-connect__title">Integrations Portal</h6>
      <p class="ip-connect__desc">Mirror your completed payments to the tools connected in your Portal.</p>

      <div class="ip-connect__status">
        <span v-if="loading" class="ip-status">
          <span class="ip-dot ip-dot--idle"></span>
          <span class="ip-status__text ip-status__text--muted">Checking connection&hellip;</span>
        </span>

        <span v-else-if="!reachable" class="ip-status">
          <span class="ip-dot ip-dot--idle"></span>
          <span class="ip-status__text ip-status__text--muted">{{ unreachableMessage }}</span>
        </span>

        <span v-else-if="connected" class="ip-status">
          <span class="ip-dot ip-dot--live"></span>
          <span class="ip-status__text ip-status__text--on">Connected</span>
          <span class="ip-status__text ip-status__text--muted">&middot; mirroring payments</span>
        </span>

        <span v-else class="ip-status">
          <span class="ip-dot ip-dot--idle"></span>
          <span class="ip-status__text ip-status__text--muted">Not connected</span>
        </span>
      </div>
    </div>

    <div class="ip-connect__action">
      <template v-if="!loading && reachable && connected">
        <!-- Plain form POST: the server mints a single-use SSO ticket and 302s
             this new tab to the Portal (XHR + window.open would be popup-blocked). -->
        <form method="post" action="/integrations_portal/session" target="_blank" @submit="onOpenPortal">
          <input type="hidden" name="authenticity_token" :value="csrfToken">
          <button
            type="submit"
            class="btn ip-connect__btn"
            :class="{ disabled: opening }"
            :disabled="opening"
          >
            <i class="fas fa-external-link-alt"></i>
            Open Portal
          </button>
        </form>

        <a
          class="btn-small btn-grey"
          :class="{ disabled: disabled }"
          @click="disconnect"
        >Disconnect</a>
      </template>

      <a
        v-else-if="!loading && reachable"
        class="btn ip-connect__btn"
        :class="{ disabled: disabled }"
        @click="connect"
      >
        <i class="fas fa-plug"></i>
        {{ connectLabel }}
      </a>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    // Seed only. connect/disconnect and the status check all write to it, and a
    // prop written by its own component is overwritten the next time the parent
    // renders - so the live value lives in data, as pay_setting_switch does with
    // initialState.
    initialConnected: { type: Boolean, default: false }
  },
  data() {
    return {
      connected: this.initialConnected,
      loading: true,
      reachable: true,
      disabled: false,
      opening: false,
      status: null,
      reason: null
    }
  },
  computed: {
    connectLabel() {
      return this.status === "disconnected" ? "Reconnect" : "Connect"
    },
    // "misconfigured" means our Portal credentials are rejected — retrying
    // never clears it, so don't tell the merchant to try again.
    unreachableMessage() {
      return this.reason === "misconfigured"
        ? "Portal connection needs attention — please contact support."
        : "Portal unavailable — try again shortly."
    },
    csrfToken() {
      const meta = document.querySelector('meta[name="csrf-token"]')
      return meta ? meta.getAttribute("content") : ""
    }
  },
  mounted() {
    this.checkStatus()
  },
  methods: {
    // Let the native form submit proceed, then briefly disable the button so a
    // double-click can't mint a second single-use ticket / spawn another tab.
    onOpenPortal() {
      this.opening = true
      setTimeout(() => { this.opening = false }, 3000)
    },
    checkStatus() {
      this.$http.get("/api/v1/integrations_portal/connection").then(response => {
        this.applyState(response.body)
        this.loading = false
      }, () => {
        this.reachable = false
        this.reason = "unavailable"
        this.loading = false
      })
    },
    connect() {
      if (this.disabled) { return }

      this.disabled = true
      this.$http.post("/api/v1/integrations_portal/connection").then(response => {
        this.connected = response.body.connected
        this.status = "connected"
        this.disabled = false
        this.notifySuccess("Integrations Portal connected")
      }, error => {
        this.disabled = false
        this.notifyError(this.errorMessage(error, "Connection failed"))
      })
    },
    disconnect() {
      if (this.disabled) { return }
      if (!window.confirm("Disconnect the Integrations Portal? Transactions stop syncing until you reconnect.")) { return }

      this.disabled = true
      this.$http.delete("/api/v1/integrations_portal/connection").then(response => {
        this.connected = response.body.connected
        this.status = "disconnected"
        this.disabled = false
        this.notifySuccess("Integrations Portal disconnected")
      }, error => {
        this.disabled = false
        this.notifyError(this.errorMessage(error, "Disconnect failed"))
      })
    },
    applyState(body) {
      this.connected = body.connected
      this.reachable = body.reachable
      this.status = body.status
      this.reason = body.reason
    },
    errorMessage(error, fallback) {
      const messages = error.body && error.body.error_messages
      return messages && messages[0] ? messages[0] : fallback
    }
  }
}
</script>

<style scoped>
.ip-connect {
  display: flex;
  align-items: center;
  gap: 18px;
}

.ip-connect__emblem {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: rgba(0, 171, 142, 0.1);
  color: #00ab8e;
}

.ip-connect__emblem i {
  font-size: 19px;
}

.ip-connect__body {
  flex: 1 1 auto;
  min-width: 0;
}

.ip-connect__title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;
  color: #1a2b32;
}

.ip-connect__desc {
  margin: 2px 0 0;
  font-size: 13px;
  line-height: 1.45;
  color: #697386;
}

.ip-connect__status {
  margin-top: 9px;
}

.ip-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.ip-status__text {
  font-size: 12.5px;
  letter-spacing: 0.01em;
}

.ip-status__text--on {
  font-weight: 700;
  color: #00876f;
}

.ip-status__text--muted {
  color: #8792a2;
}

/* Signature: a live connection dot. Emerald + soft pulse when syncing. */
.ip-dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.ip-dot--idle {
  background: #c4ccd3;
}

.ip-dot--live {
  background: #00ab8e;
  box-shadow: 0 0 0 0 rgba(0, 171, 142, 0.5);
  animation: ip-pulse 2.2s ease-out infinite;
}

@keyframes ip-pulse {
  70% { box-shadow: 0 0 0 6px rgba(0, 171, 142, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 171, 142, 0); }
}

.ip-connect__action {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}

/* Connect button uses the standard Materialize .btn — the theme color is already emerald (#00ab8e)
   and button labels are uppercase app-wide; only the inline icon spacing needs a tweak. */
.ip-connect__btn i {
  margin-right: 7px;
  font-size: 13px;
}

@media (max-width: 600px) {
  .ip-connect {
    flex-wrap: wrap;
  }

  .ip-connect__action {
    flex-basis: 100%;
    margin-left: 64px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ip-dot--live {
    animation: none;
  }
}
</style>
